/**
 * Converts integer pence to GBP display format.
 * 
 * Constitution Rule 1: All monetary values stored as integer pence; display via (pence || 0) / 100
 * rounded half-up to exactly 2 decimals, retaining trailing zeros.
 * 
 * Validates: Requirements 8.1, 8.2, 8.3, 8.4
 */

/**
 * Convert pence to a GBP display string with exactly 2 decimal places.
 * 
 * - Computes `(pence || 0) / 100` rounded half-up to 2 decimals
 * - Retains trailing zeros (e.g., "55.00" not "55")
 * - Negative values render a single leading minus (e.g., "-12.50")
 * - null/undefined/non-numeric → "0.00"
 * - Returns number only (no £ prefix); callers add £
 * 
 * @param pence - The monetary value in pence, or any value
 * @returns A string with exactly 2 decimal places (e.g. "55.01", "-12.50", "0.00")
 */
export function toGbpDisplay(pence: unknown): string {
  // Handle null/undefined/non-numeric: treat as 0
  if (pence === null || pence === undefined) {
    return '0.00';
  }

  // Coerce to number; if non-numeric, treat as 0
  const numericPence = Number(pence);
  if (isNaN(numericPence) || !isFinite(numericPence)) {
    return '0.00';
  }

  // Use integer portion only (ignore fractional pence)
  const integerPence = Math.floor(Math.abs(numericPence));
  const pounds = integerPence / 100;

  // Round half-up to 2 decimals: add 0.0005, then truncate
  const rounded = Math.floor(pounds * 100 + 0.5) / 100;

  // Apply sign
  const signed = numericPence < 0 ? -rounded : rounded;

  // Format with exactly 2 decimal places
  return signed.toFixed(2);
}
